import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  viewChild
} from '@angular/core';
import { ToastController, IonicModule } from '@ionic/angular';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { SupportWidgetService } from 'src/app/core/services/support-widget.service';
import { SupportService } from 'src/app/core/services/support.service';
import { LocalAuthService } from 'src/app/core/services/auth.service';
import { TenantService } from 'src/app/core/services/tenant.service';
import { LanguageService } from 'src/app/core/services/language.service';
import {
  SupportConversation,
  SupportMessage,
  PendingAttachment,
} from 'src/app/core/models/support.model';
import { SupportMessageReceivedEvent } from 'src/app/core/models/events';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, DatePipe } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';

/**
 * Global floating support widget (launcher + docked panel). Presentation only — all
 * data, realtime and read-tracking come from the existing SupportService. Always runs
 * in user mode (isAdmin = false); admins keep the full-page inbox for triage.
 */
@Component({
    selector: 'app-support-widget',
    templateUrl: './support-widget.component.html',
    styleUrls: ['./support-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        AsyncPipe,
        DatePipe,
        TranslatePipe,
    ],
})
export class SupportWidgetComponent implements OnInit, OnDestroy {
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  readonly threadScroll = viewChild<ElementRef<HTMLElement>>('threadScroll');

  private readonly widget = inject(SupportWidgetService);
  private readonly support = inject(SupportService);
  private readonly auth = inject(LocalAuthService);
  private readonly tenant = inject(TenantService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toast = inject(ToastController);
  private readonly lang = inject(LanguageService);
  private readonly sub = new Subscription();

  readonly open$ = this.widget.open$;
  readonly view$ = this.widget.view$;
  readonly conversations$ = this.support.conversations$;
  readonly unreadCount$ = this.support.unreadCount$;

  /**
   * Show the widget only for authenticated, non-admin users with an active tenant.
   * Admins handle support through the full-page inbox, so the launcher is hidden for them.
   */
  readonly visible$ = combineLatest([
    this.auth.isAuthenticated$,
    this.tenant.activeTenant$,
  ]).pipe(
    map(([authed, tenant]) => {
      const isAdmin = !!tenant?.isAdmin;
      return !!authed && !!tenant && !isAdmin;
    }),
  );

  activeConversation: SupportConversation | null = null;
  messages: SupportMessage[] = [];
  isLoadingThread = false;
  isSending = false;
  isEscalating = false;
  /** Shows a "Karl is generating…" bubble after sending, until his reply arrives. */
  isKarlTyping = false;
  private karlTypingTimeout?: ReturnType<typeof setTimeout>;

  newSubject = '';
  isCreating = false;

  messageText = '';
  pendingAttachments: PendingAttachment[] = [];

  currentUserId: string | null = null;

  /** The conversation this widget currently has open on screen (for view-tracking). */
  private viewedConversationId: string | null = null;

  ngOnInit(): void {
    this.sub.add(
      this.auth.user$.subscribe((u) => {
        this.currentUserId = u?.sub ?? null;
      }),
    );

    // Refresh the conversation list whenever the widget is opened, and (on mobile)
    // pin the full-screen panel to the visual viewport so the on-screen keyboard
    // doesn't shove the whole modal upward.
    this.sub.add(
      this.widget.open$.subscribe((open) => {
        if (open) {
          void this.support.loadConversations(false);
          this.setupKeyboardViewport();
        } else {
          this.teardownKeyboardViewport();
        }
      }),
    );

    // Track whether the user is actively viewing a thread (panel open on the thread view),
    // so incoming replies for it aren't re-flagged unread.
    this.sub.add(
      combineLatest([
        this.widget.open$,
        this.widget.view$,
        this.widget.activeConversationId$,
      ]).subscribe(([open, view, id]) => {
        const viewing = open && view === 'thread' ? id : null;
        if (viewing === this.viewedConversationId) return;
        if (this.viewedConversationId) this.support.leaveConversation(this.viewedConversationId);
        this.viewedConversationId = viewing;
        if (viewing) this.support.enterConversation(viewing);
      }),
    );

    this.sub.add(
      this.widget.activeConversationId$.subscribe((id) => {
        if (id) {
          void this.loadThread(id);
        } else {
          this.activeConversation = null;
          this.messages = [];
          this.messageText = '';
          this.pendingAttachments = [];
          this.stopKarlTyping();
          this.cdr.markForCheck();
        }
      }),
    );

    this.sub.add(
      this.support.observe(SupportMessageReceivedEvent).subscribe((event) => {
        const e = event as SupportMessageReceivedEvent & {
          conversationId: string;
          messageId: string;
          senderId: string;
          isAdminMessage: boolean;
          content: string;
          attachments?: SupportMessage['attachments'];
          createdAtUtc: string;
        };
        if (!this.activeConversation || e.conversationId !== this.activeConversation.conversationId) {
          return;
        }
        // Karl's (or an admin's) reply has arrived — stop the generating indicator.
        if (e.senderId !== this.currentUserId) {
          this.stopKarlTyping();
        }
        const msg: SupportMessage = {
          messageId: e.messageId,
          conversationId: e.conversationId,
          senderId: e.senderId,
          isAdminMessage: e.isAdminMessage,
          isAiMessage: e.isAiMessage,
          suggestsClose: e.suggestsClose,
          content: e.content,
          attachments: e.attachments ?? [],
          createdAtUtc: e.createdAtUtc,
        };
        if (this.addMessage(msg)) {
          this.scrollToBottom();
          this.cdr.markForCheck();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.viewedConversationId) this.support.leaveConversation(this.viewedConversationId);
    clearTimeout(this.karlTypingTimeout);
    this.teardownKeyboardViewport();
    this.sub.unsubscribe();
  }

  // ── Mobile keyboard handling ─────────────────────────────────────────────────
  // On mobile the open panel is full-screen and fixed-positioned. When the virtual
  // keyboard appears, iOS (Safari/PWA) shrinks the visual viewport and scrolls the
  // page, pushing the whole modal up. By sizing the panel to the visual viewport
  // (height) and following its offset (top), the message input stays just above the
  // keyboard and there is nothing left for the browser to scroll. No-op on desktop.
  private vvHandler?: () => void;

  private setupKeyboardViewport(): void {
    const vv = window.visualViewport;
    if (!vv || !window.matchMedia('(max-width: 600px)').matches) return;

    const root = document.documentElement;
    this.vvHandler = () => {
      root.style.setProperty('--sw-vv-height', `${vv.height}px`);
      root.style.setProperty('--sw-vv-top', `${vv.offsetTop}px`);
    };
    vv.addEventListener('resize', this.vvHandler);
    vv.addEventListener('scroll', this.vvHandler);
    this.vvHandler();
  }

  private teardownKeyboardViewport(): void {
    const vv = window.visualViewport;
    if (vv && this.vvHandler) {
      vv.removeEventListener('resize', this.vvHandler);
      vv.removeEventListener('scroll', this.vvHandler);
    }
    this.vvHandler = undefined;

    const root = document.documentElement;
    root.style.removeProperty('--sw-vv-height');
    root.style.removeProperty('--sw-vv-top');
  }

  // ── Launcher / navigation ──────────────────────────────────────────────────
  toggle(): void {
    this.widget.toggle();
  }

  close(): void {
    this.widget.close();
  }

  backToHome(): void {
    this.widget.backToHome();
  }

  openConversation(c: SupportConversation): void {
    this.widget.openConversation(c.conversationId);
  }

  // ── New request ────────────────────────────────────────────────────────────
  async startNew(): Promise<void> {
    const subject = this.newSubject.trim();
    if (!subject || this.isCreating) return;

    this.isCreating = true;
    this.cdr.markForCheck();
    try {
      const conv = await this.support.createConversation(subject);
      this.newSubject = '';
      this.widget.openConversation(conv.conversationId);
    } catch (err) {
      console.error('Failed to create support conversation', err);
    } finally {
      this.isCreating = false;
      this.cdr.markForCheck();
    }
  }

  onNewSubjectEnter(event: Event): void {
    event.preventDefault();
    void this.startNew();
  }

  // ── Thread ─────────────────────────────────────────────────────────────────
  private async loadThread(conversationId: string): Promise<void> {
    this.isLoadingThread = true;
    this.messageText = '';
    this.pendingAttachments = [];
    this.stopKarlTyping();
    this.cdr.markForCheck();
    try {
      const [messages, conversations] = await Promise.all([
        this.support.loadMessages(conversationId, false),
        this.support.loadConversations(false),
      ]);
      this.messages = messages;
      this.activeConversation =
        conversations.find((c) => c.conversationId === conversationId) ?? null;
      // Re-apply read state after the list refresh (loadConversations races the
      // GET /messages read side-effect and can clobber the optimistic mark-read).
      this.support.markConversationRead(conversationId, false);
      this.scrollToBottom();
    } finally {
      this.isLoadingThread = false;
      this.cdr.markForCheck();
    }
  }

  onComposerEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    void this.send();
  }

  async send(): Promise<void> {
    const text = this.messageText.trim();
    if (
      (!text && this.pendingAttachments.length === 0) ||
      this.isSending ||
      !this.activeConversation
    ) {
      return;
    }

    const conversationId = this.activeConversation.conversationId;
    this.isSending = true;
    this.cdr.markForCheck();
    try {
      for (const pa of this.pendingAttachments) {
        pa.status = 'uploading';
        await this.support.uploadFile(pa);
        pa.status = 'done';
      }
      const attachments = this.pendingAttachments.map((pa) => ({
        attachmentId: pa.attachmentId,
        fileName: pa.fileName,
        contentType: pa.contentType,
        blobPath: pa.blobPath,
      }));
      const msg = await this.support.sendMessage(conversationId, text || '📎', attachments, false);
      this.addMessage(msg);
      this.messageText = '';
      this.pendingAttachments = [];
      // Karl replies over SignalR — show a generating indicator until it lands.
      if (this.isAiConversation) {
        this.startKarlTyping();
      }
      this.scrollToBottom();
    } catch (err) {
      console.error('Failed to send support message', err);
      // Reset stuck "uploading" chips so the spinner stops and the user can retry.
      for (const pa of this.pendingAttachments) {
        if (pa.status !== 'done') pa.status = 'pending';
      }
      await this.showError(
        this.lang.translate(
          this.pendingAttachments.length
            ? 'support.widget.errorUpload'
            : 'support.widget.errorSend',
        ),
      );
    } finally {
      this.isSending = false;
      this.cdr.markForCheck();
    }
  }

  private startKarlTyping(): void {
    this.isKarlTyping = true;
    this.cdr.markForCheck();
    this.scrollToBottom();
    // Safety net: never leave the indicator spinning if no reply ever arrives.
    clearTimeout(this.karlTypingTimeout);
    this.karlTypingTimeout = setTimeout(() => this.stopKarlTyping(), 30_000);
  }

  private stopKarlTyping(): void {
    clearTimeout(this.karlTypingTimeout);
    this.karlTypingTimeout = undefined;
    if (!this.isKarlTyping) return;
    this.isKarlTyping = false;
    this.cdr.markForCheck();
  }

  // ── Escalation / close ───────────────────────────────────────────────────────
  get isAiConversation(): boolean {
    return this.activeConversation?.status === 'ai';
  }

  async escalate(): Promise<void> {
    if (!this.activeConversation || this.isEscalating) return;
    this.isEscalating = true;
    this.cdr.markForCheck();
    try {
      const conv = await this.support.escalateConversation(this.activeConversation.conversationId);
      this.activeConversation = { ...this.activeConversation, ...conv };
    } catch (err) {
      console.error('Failed to escalate support conversation', err);
      await this.showError(this.lang.translate('support.widget.errorEscalate'));
    } finally {
      this.isEscalating = false;
      this.cdr.markForCheck();
    }
  }

  async confirmClose(): Promise<void> {
    if (!this.activeConversation) return;
    try {
      const conv = await this.support.closeConversation(this.activeConversation.conversationId);
      this.activeConversation = { ...this.activeConversation, ...conv };
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Failed to close support conversation', err);
      await this.showError(this.lang.translate('support.widget.errorClose'));
    }
  }

  // ── Attachments ────────────────────────────────────────────────────────────
  openFilePicker(): void {
    this.fileInput()?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) void this.addFiles(Array.from(input.files));
    input.value = '';
  }

  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) void this.addFiles(files);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files) void this.addFiles(Array.from(files));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  removeAttachment(pa: PendingAttachment): void {
    this.pendingAttachments = this.pendingAttachments.filter(
      (a) => a.attachmentId !== pa.attachmentId,
    );
  }

  private async addFiles(files: File[]): Promise<void> {
    if (!this.activeConversation) return;
    const conversationId = this.activeConversation.conversationId;
    for (const file of files) {
      try {
        const upload = await this.support.requestUploadUrl(
          conversationId,
          file.name,
          file.type || 'application/octet-stream',
        );
        this.pendingAttachments = [
          ...this.pendingAttachments,
          {
            attachmentId: upload.attachmentId,
            file,
            fileName: file.name,
            contentType: file.type,
            blobPath: upload.blobPath,
            uploadUrl: upload.uploadUrl,
            status: 'pending',
          },
        ];
      } catch (err) {
        console.error('Failed to request upload URL for', file.name, err);
        await this.showError(this.lang.translate('support.widget.errorAdd', { name: file.name }));
      }
    }
    this.cdr.markForCheck();
  }

  private async showError(message: string): Promise<void> {
    const toast = await this.toast.create({
      message,
      duration: 3500,
      color: 'danger',
      position: 'bottom',
    });
    await toast.present();
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  isImage(contentType: string): boolean {
    return contentType.startsWith('image/');
  }

  trackByConv(_: number, c: SupportConversation): string {
    return c.conversationId;
  }

  trackByMsg(_: number, m: SupportMessage): string {
    return m.messageId;
  }

  trackByAtt(_: number, a: PendingAttachment): string {
    return a.attachmentId;
  }

  /**
   * Add a message to the open thread, keeping it ordered by server timestamp.
   *
   * Karl's reply is pushed over SignalR from inside the same POST /messages request
   * that creates the user's question, so the realtime event can land *before* that
   * POST resolves and appends the question locally. A blind append would then render
   * Karl's answer above the question it answers (until a reload re-sorts from the API).
   * createdAtUtc is a server clock for both messages, so ordering by it is stable.
   *
   * @returns true if the message was new (added), false if it was already present.
   */
  private addMessage(msg: SupportMessage): boolean {
    if (this.messages.some((m) => m.messageId === msg.messageId)) return false;
    this.messages = [...this.messages, msg].sort(
      (a, b) =>
        new Date(a.createdAtUtc).getTime() - new Date(b.createdAtUtc).getTime() ||
        a.messageId.localeCompare(b.messageId),
    );
    return true;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.threadScroll()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 60);
  }
}
